import { useFetch } from "@/lib/axios.config";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ProfilePayload } from "./profile.schema";
import { useTranslation } from "react-i18next";
import showCustomToast from "@/shared/utils/showCustomToast.ts";

export interface FileUploadResponse {
  id: string;
  url: string;
  name: string;
}

export interface FileUploadParams {
  file: File;
  purpose: string;
  type: string;
}

export const uploadFile = async ({
  file,
  purpose,
  type,
}: FileUploadParams): Promise<FileUploadResponse> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await useFetch.post<FileUploadResponse>(
    "/common/FileUpload",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        purpose,
        type,
      },
    }
  );

  return response.data;
};

export const useFileUpload = () => {
  return useMutation<FileUploadResponse, Error, FileUploadParams>({
    mutationKey: ["uploadFile"],
    mutationFn: uploadFile,
  });
};

export const updateProfile = async (data: Partial<ProfilePayload>) => {
  const response = await useFetch.patch<ProfilePayload>(
    "/users/ProfileUpdate",
    data
  );
  return response.data;
};

export const useProfileUpdate = (isAuth?: boolean) => {
  const { t } = useTranslation();
  return useMutation<ProfilePayload, Error, Partial<ProfilePayload>>({
    mutationFn: updateProfile,
    onSuccess: () => {
      if (!isAuth) {
        showCustomToast(t("profile_updated_successfully"), "success");
      }
    },
    onError: () => {
      if (isAuth) {
        showCustomToast(t("auth_failed"), "error");
        return;
      }
      showCustomToast(t("profile_update_failed"), "error");
    },
    retry: 3,
    retryDelay: 1000,
  });
};

export interface Region {
  id: string;
  name: string;
  soato: string;
}

export interface District {
  id: string;
  name: string;
  region_id: string;
}

// Fetch regions API function
export const fetchRegions = async () => {
  const response = await useFetch.get<Region[]>("/common/RegionList");
  const regionsData = response.data;

  // Sort the regions before returning
  return regionsData.sort((a, b) => {
    // Qoraqalpog'iston Respublikasi should be first
    if (a.soato === "1735") return -1;
    if (b.soato === "1735") return 1;

    // Toshkent shahri should be last
    if (a.soato === "1726") return 1;
    if (b.soato === "1726") return -1;

    // For all other regions, sort alphabetically
    return a.name.localeCompare(b.name);
  });
};

// Fetch districts API function
export const fetchDistricts = async (regionId: string): Promise<District[]> => {
  if (!regionId) return [];

  const response = await useFetch.get<District[]>(
    `/common/DistrictList/${regionId}`
  );
  const districts = response.data;
  // Sort the districts before returning
  return districts.sort((a, b) => a.name.localeCompare(b.name));
};

// Custom hook for fetching regions
export const useRegions = () => {
  return useQuery<Region[], Error>({
    queryKey: ["regions"],
    queryFn: fetchRegions,
    staleTime: 1000 * 60 * 60, // 1 hour
    retry: 3,
    retryDelay: 1000,
  });
};

// Custom hook for fetching districts by region ID
export const useDistricts = (regionId: string | undefined) => {
  return useQuery<District[], Error>({
    queryKey: ["districts", regionId],
    queryFn: () => fetchDistricts(regionId || ""),
    enabled: !!regionId,
    staleTime: 1000 * 60 * 60, // 1 hour
    retry: 2,
    retryDelay: 1000,
  });
};
